// REV-18627: konkreter Pfad statt Barrel — zirkulaerer Import in Node (siehe contract-C1.md)
import { NumberUtils } from '../../../../aumico-lib-core-fe/src/lib/utils/number-utils';
import { IAccount, IBalanceSwitchAccount, IFinancialStatementSource } from '@modeso/types__aumico-lib-annualreport-be';
import Debug from 'debug';
import { BalanceSwitchAccountService } from '../../../../aumico-lib-templates-fe/src/lib/services/balance-switch-account.service';
import { ISheetRow } from '../interfaces/ISheetRow';
const debug = Debug('aumico:aumico-lib-annualreports-fe:SwitchAccounts');

export class RawAccountsHelper {
    private noPreviousYearData: boolean;
    private isPreviousYearDataTakenFromPreviousAnnualReport: boolean;
    private previousYearDuplicateAccountNumber: Set<string>;
    private currentYearDuplicateAccountNumber: Set<string>;
    private previousAccounts: IAccount[];
    private previousBalanceSwitchAccounts: IBalanceSwitchAccount[];
    private financialStatementSource: IFinancialStatementSource;
    private balanceSwitchAccountsService: BalanceSwitchAccountService;
    constructor(
        noPreviousYearData: boolean,
        isPreviousYearDataTakenFromPreviousAnnualReport: boolean,
        financialStatementSource: IFinancialStatementSource,
    ) {
        this.noPreviousYearData = noPreviousYearData;
        this.isPreviousYearDataTakenFromPreviousAnnualReport = isPreviousYearDataTakenFromPreviousAnnualReport;
        this.previousYearDuplicateAccountNumber = null;
        this.currentYearDuplicateAccountNumber = null;
        this.previousAccounts = [];
        this.previousBalanceSwitchAccounts = [];
        this.balanceSwitchAccountsService = new BalanceSwitchAccountService();
        this.financialStatementSource = financialStatementSource;
    }

    public setDuplicateAccountsNumberSets(
        previousYearDuplicateAccounts: Set<string>,
        currentYearDuplicateAccounts: Set<string>,
    ): void {
        this.previousYearDuplicateAccountNumber = previousYearDuplicateAccounts;
        this.currentYearDuplicateAccountNumber = currentYearDuplicateAccounts;
    }

    public setPreviousAccounts(previousAccounts: IAccount[]): void {
        this.previousAccounts = [...previousAccounts];
    }
    public setFinancialStatementSource(financialStatementSource: IFinancialStatementSource): void {
        this.financialStatementSource = financialStatementSource;
    }

    public setPreviousBalanceSwitchAccounts(previousBalanceSwitchAccounts: IBalanceSwitchAccount[]): void {
        this.previousBalanceSwitchAccounts = [...previousBalanceSwitchAccounts];
    }

    public getAccountClosingBalanceValue(closingColumnCellIndex: number, row: ISheetRow): number {
        const closingBalanceCellValue = row.cells[closingColumnCellIndex];
        if (closingBalanceCellValue == null) {
            return 0;
        }
        return NumberUtils.removeThousandSeparator(closingBalanceCellValue, 0);
    }

    public wasPreviousYearBalanceSwitchAccount(
        accountNumber: string,
    ): boolean {

        if (this.noPreviousYearData) {
            return false;
        } else if (!this.isPreviousYearDataTakenFromPreviousAnnualReport) {
            return false;
        } else if (this.isPreviousYearDataTakenFromPreviousAnnualReport) {
            const previousBalanceSwitchAccount = this.previousAccounts.find(
                (acc) => acc.accountNumber === accountNumber && acc.isBalanceSwitchAccount === true,
            );
            if (previousBalanceSwitchAccount != null) {
                return true;
            }
        }
        return false;
    }

    public getAccountOpeningBalanceValue(
        accountNumberCellIndex: number,
        openingColumnCellIndex: number,
        closingColumnCellIndex: number,
        openingColumnCellIndexForDuplicateAccounts: number,
        row: ISheetRow,
        shareholderColumnCellIndex?: number,
        closingBalance?: number,
        accountNumber?: string,
    ): { openingBalance: number; closingBalance: number; tookFromExcel: boolean } {

        if (this.noPreviousYearData) {
            return { openingBalance: 0, closingBalance: closingBalance, tookFromExcel: true };
        } else if (!this.isPreviousYearDataTakenFromPreviousAnnualReport) {
            const val = this.getOpeningValueFromCurrentYear(openingColumnCellIndex, row);
            return { openingBalance: val, closingBalance: closingBalance, tookFromExcel: true };
        } else if (this.isPreviousYearDataTakenFromPreviousAnnualReport) {
            return this.getOpeningValueFromPreviousYear(
                accountNumberCellIndex,
                closingColumnCellIndex,
                row,
                shareholderColumnCellIndex,
                closingBalance,
                accountNumber,
                openingColumnCellIndexForDuplicateAccounts
            );
        }
        return { openingBalance: 0, closingBalance: closingBalance, tookFromExcel: false };
    }

    private getOpeningValueFromCurrentYear(openingColumnCellIndex: number, row: ISheetRow): number {
        const openingBalanceCellValue = row.cells[openingColumnCellIndex];
        if (openingBalanceCellValue == null) {
            return 0;
        }
        return NumberUtils.removeThousandSeparator(openingBalanceCellValue, 0);
    }

    private getOpeningValueFromPreviousYear(
        accountNumberCellIndex: number,
        closingColumnCellIndex: number,
        row: ISheetRow,
        shareholderColumnCellIndex?: number,
        newClosingBalance?: number,
        accountNumber?: string,
        openingColumnCellIndexForDuplicateAccounts?: number,
    ): { openingBalance: number; closingBalance: number; tookFromExcel: boolean } {
        if (accountNumber == null) {
            accountNumber = row.cells[accountNumberCellIndex];
        }

        if (accountNumber == null) {
            accountNumber = '';
        }

        if (
            !this.previousYearDuplicateAccountNumber.has(accountNumber) &&
            !this.currentYearDuplicateAccountNumber.has(accountNumber)
        ) {
            const foundMatchingAccount = this.previousAccounts.find(
                (account) => account.accountNumber === accountNumber,
            );

            if (foundMatchingAccount != null) {
                let openingValue = 0;

                if (NumberUtils.isFinite(foundMatchingAccount.closingBalance)) {
                    openingValue = NumberUtils.roundNumber(foundMatchingAccount.calculatedClosingBalance);
                }
                return { openingBalance: openingValue, closingBalance: newClosingBalance, tookFromExcel: false };
            }

            // Identify the item to be taken from excel since no matching account is found in previous year accounts
            return { openingBalance: 0, closingBalance: newClosingBalance, tookFromExcel: true };
        } else {
            const previousBalanceSwitchAccount = this.previousBalanceSwitchAccounts.find(
                (acc) => acc.accountNumber === accountNumber,
            );

            if (previousBalanceSwitchAccount != null) {
                // The previous year's switch account closing becomes this year's opening (Vorjahr).
                // BUGFIX (REV-21304): use the trustworthy SIGNED closing, NOT the raw `closingBalance`.
                // For reports whose closingBalance sign was corrupted by the double-save bug (+1'000
                // instead of -1'000), carrying the raw value would flip the carried opening's sign and
                // place it on the wrong leg during autofill (a liability carried onto the asset side).
                //
                // BUGFIX (REV-21304, 3rd-year carry): reconstruct the previous year's closing from its
                // split LEGS first. `getReliableRawBalances` trusts `originalClosingBalance`, but that
                // snapshot is frozen at the FIRST split and propagated unchanged across years, so from
                // the 2nd switched year on it is STALE (a year-3 carry read the year-1 closing instead
                // of the year-2 closing). The legs always hold the current year's magnitudes, so they
                // give the correct signed value; fall back to the summary only when the legs are absent.
                const reliable =
                    BalanceSwitchAccountService.getSignedBalancesFromLegs(
                        previousBalanceSwitchAccount,
                        this.previousAccounts,
                    ) ?? BalanceSwitchAccountService.getReliableRawBalances(previousBalanceSwitchAccount);
                const newOpeningValue = reliable.closingBalance;
                return { openingBalance: newOpeningValue, closingBalance: newClosingBalance, tookFromExcel: false };
            }

            let accountShareholder = row.cells[shareholderColumnCellIndex];

            let closingValueOfCurrentYear = 0;
            const closingBalanceCellValue = row.cells[closingColumnCellIndex];
            if (closingBalanceCellValue != null) {
                closingValueOfCurrentYear = NumberUtils.removeThousandSeparator(closingBalanceCellValue, 0);
            }

            let openingValueOfCurrentYearFromExcel = 0;
            const openingBalanceCellValue = row.cells[openingColumnCellIndexForDuplicateAccounts];
            if (openingBalanceCellValue != null) {
                openingValueOfCurrentYearFromExcel = NumberUtils.removeThousandSeparator(openingBalanceCellValue, 0);
            }

            const val = this.getOpeningValueOfDuplicateAccountFromPreviousYear(
                accountNumber,
                closingValueOfCurrentYear,
                accountShareholder,
                openingValueOfCurrentYearFromExcel,
            );
            return { openingBalance: val.value, closingBalance: newClosingBalance, tookFromExcel: val.tookFromExcel };
        }
    }

    private getOpeningValueOfDuplicateAccountFromPreviousYear(
        accountNumber: string,
        currentCloseValue: number,
        shareholder?: string,
        openingValueOfCurrentYearFromExcel?: number,
    ): { value: number, tookFromExcel: boolean } {
        if (
            this.previousYearDuplicateAccountNumber.has(accountNumber) &&
            !this.currentYearDuplicateAccountNumber.has(accountNumber)
        ) {
            // in previous year there were duplicate accounts with this account number but in current year it becomes non duplicate
            return this.getOpeningValueOfDuplicateAccount(accountNumber, undefined, undefined, openingValueOfCurrentYearFromExcel);
        } else if (
            this.previousYearDuplicateAccountNumber.has(accountNumber) &&
            this.currentYearDuplicateAccountNumber.has(accountNumber)
        ) {
            // in previous there there were duplicate accounts with this account number and in the current year too
            return this.getOpeningValueOfDuplicateAccount(accountNumber, currentCloseValue, shareholder, openingValueOfCurrentYearFromExcel);
        } else if (
            !this.previousYearDuplicateAccountNumber.has(accountNumber) &&
            this.currentYearDuplicateAccountNumber.has(accountNumber)
        ) {
            // in previous year account number wasn't a duplicate but in the current year it is a duplicate account
            // In this case, we need to get the value from the excel since in previous year there is only one account with this number
            return { value: openingValueOfCurrentYearFromExcel ?? 0, tookFromExcel: true };
        } else {
            // duplicate accounts exist in current year but not in previous
            // impossible case from business perspective
            return { value: openingValueOfCurrentYearFromExcel ?? 0, tookFromExcel: true };
        }
    }

    private getOpeningValueOfDuplicateAccount(
        accountNumber: string,
        accountCurrentYearClosingValue?: number,
        shareholder?: string,
        openingValueOfCurrentYearFromExcel?: number,
    ): { value: number, tookFromExcel: boolean } {
        if (shareholder != null && shareholder !== '') {
            const previousYearAccount = this.previousAccounts.find(
                (account) => account.accountNumber === accountNumber && account.shareholder === shareholder,
            );
            if (previousYearAccount != null) {
                if (NumberUtils.isFinite(previousYearAccount.calculatedClosingBalance)) {
                    return { value: NumberUtils.roundNumber(previousYearAccount.calculatedClosingBalance), tookFromExcel: false };
                }
                return { value: 0, tookFromExcel: false };
            }

            return { value: 0, tookFromExcel: false };
        }

        const previousYearDuplicateAccounts = this.previousAccounts.filter(
            (account) => account.accountNumber === accountNumber,
        );

        // If previousYearDuplicateAccounts is 1, return its calculatedClosingBalance
        // That means there is only one account with this account number in the previous year
        if (previousYearDuplicateAccounts.length === 1) {
            return { value: NumberUtils.roundNumber(previousYearDuplicateAccounts[0].calculatedClosingBalance), tookFromExcel: false };
        }

        // If previousYearDuplicateAccounts is empty, return value from excel as opening value
        // That means there are no accounts with this account number in the previous year
        if (previousYearDuplicateAccounts.length === 0) {
            return { value: openingValueOfCurrentYearFromExcel ?? 0, tookFromExcel: true };
        }

        // Otherwise, there are multiple duplicate accounts in the previous year
        // We try to identify the correct opening balance based on the name of the account
        const previousYearDuplicateAccountsWithSameName = previousYearDuplicateAccounts.filter(
            (account) => account.name === previousYearDuplicateAccounts[0].name,
        );

        if (previousYearDuplicateAccountsWithSameName.length === 1) {
            return { value: NumberUtils.roundNumber(previousYearDuplicateAccountsWithSameName[0].calculatedClosingBalance), tookFromExcel: false };
        }

        // TODO: Further improvements can be made here to identify the correct account based on more criteria
        // This can be:
        // - closing balance of previous year equals opening balance of current year (openingValueOfCurrentYearFromExcel), etc.
        // - inverted closing balance of previous year equals opening balance of current year (openingValueOfCurrentYearFromExcel), etc.
        //   This inverted could be dangourous and leads to wrong data...
        // However, these improvements can be done later if needed because eventually at some point we should expecte the user to manually match the accounts

        // Fallback: We identify the correct opening balance based on value from excel if provided
        return { value: openingValueOfCurrentYearFromExcel ?? 0, tookFromExcel: true };
    }
}
